import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/context/CartContext';
import { useCatalog } from '@/context/CatalogContext';
import { formatRSD } from '@/lib/currency';
import { useState } from 'react';
import MiniConfigurator from './components/MiniConfigurator';

// Categories whose products go through the mini size/material configurator
// instead of a plain "add to cart" button.
const configurableCategories = ['house', 'door'];

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCart();
  const { products, loading } = useCatalog();
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const product = products.find((p) => p.id === id) || products[0];
  const isConfigurable = !!product && configurableCategories.includes(product.category);

  const handleAddToCart = () => {
    if (!product) return;
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      oldPrice: product.oldPrice ?? undefined,
      image: product.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const related = product
    ? products.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 4)
    : [];

  if (!loading && !product) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="w-full px-4 md:px-6 py-24 text-center max-w-[1450px] mx-auto">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Proizvod nije pronađen</h1>
          <p className="text-sm text-foreground-700 mb-6">Ovaj proizvod više nije dostupan ili je uklonjen.</p>
          <Link to="/shop" className="text-amber-600 hover:underline text-sm">Nazad na proizvode</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="w-full px-4 md:px-6 py-8 md:py-12 max-w-[1450px] mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-foreground-600 mb-6">
          <Link to="/" className="hover:text-amber-600 transition-colors">Početna</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/shop" className="hover:text-amber-600 transition-colors">Proizvodi</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-950">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Image */}
          <div className="bg-background-100 rounded-lg overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-auto min-h-[300px] md:min-h-[400px] object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>

          {/* Details */}
          <div>
            {product.badge && (
              <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-label font-medium rounded-full mb-4">
                {product.badge}
              </span>
            )}
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
              {product.name}
            </h1>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className={`${i < product.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-background-400'} text-sm`} />
                ))}
              </div>
              {product.reviews > 0 && (
                <span className="text-sm text-foreground-600">({product.reviews} ocena)</span>
              )}
            </div>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-label text-2xl font-bold text-foreground-950">
                {formatRSD(product.price)}
              </span>
              {product.oldPrice && (
                <span className="font-label text-base text-foreground-500 line-through">
                  {formatRSD(product.oldPrice)}
                </span>
              )}
            </div>
            <p className="text-sm text-foreground-700 leading-relaxed mb-6">
              {product.description}
            </p>

            {/* Mini Configurator for house numbers / door plates */}
            {isConfigurable && (
              <MiniConfigurator
                basePrice={product.price}
                baseProductName={product.name}
                productId={product.id}
                productImage={product.image}
                baseShapeName={product.name}
              />
            )}

            {/* Standard Add to Cart for other products */}
            {!isConfigurable && (
              <>
                {/* Quantity */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-sm font-label text-foreground-700">Količina:</span>
                  <div className="flex items-center border border-background-200 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-10 h-10 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-subtract-line" />
                    </button>
                    <span className="w-10 text-center text-sm font-label text-foreground-950">{quantity}</span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-10 h-10 flex items-center justify-center text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-add-line" />
                    </button>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <button
                    onClick={handleAddToCart}
                    className={`flex-1 py-3.5 px-8 font-label text-sm font-medium rounded-full transition-colors whitespace-nowrap flex items-center justify-center gap-2 ${
                      added
                        ? 'bg-green-600 text-white'
                        : 'bg-foreground-950 text-white hover:bg-foreground-900'
                    }`}
                  >
                    <i className={added ? 'ri-check-line' : 'ri-shopping-cart-line'} />
                    {added ? 'Dodato u korpu' : 'Dodaj u korpu'}
                  </button>
                  <Link
                    to="/cart"
                    className="flex-1 py-3.5 px-8 border border-foreground-700/30 text-foreground-700 font-label text-sm font-medium rounded-full hover:border-amber-600 hover:text-amber-600 transition-colors whitespace-nowrap text-center"
                  >
                    Idi u korpu
                  </Link>
                </div>
              </>
            )}

            {/* Trust */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {[
                { icon: 'ri-truck-line', text: 'Dostava 2-4 dana' },
                { icon: 'ri-shield-check-line', text: 'Garancija 5 godina' },
                { icon: 'ri-tools-line', text: 'Set za montažu uključen' },
                { icon: 'ri-refresh-line', text: 'Povrat 30 dana' },
              ].map((t) => (
                <div key={t.text} className="flex items-center gap-2 text-xs text-foreground-700">
                  <i className={`${t.icon} text-amber-600`} />
                  <span className="font-label">{t.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16 md:mt-24">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              Slični proizvodi
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="group">
                  <div className="bg-white rounded-lg border border-background-200 overflow-hidden hover:border-amber-300 transition-all">
                    <div className="relative aspect-square overflow-hidden bg-background-100">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-label text-sm font-medium text-foreground-950 mb-1 line-clamp-1">{p.name}</h3>
                      <div className="font-label text-sm font-bold text-foreground-950">{formatRSD(p.price)}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
